'use client';
import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import styled from '@benzinga/themetron';
import { Button, Dialog } from '@benzinga/core-ui';
import { SessionContext } from '@benzinga/session-context';
import { UserManager } from '@benzinga/user-manager';
import { emailRegEx } from '../../utils/regexes';

interface EmailCaptureProps {
  actionText: string;
  description?: string;
  handleClosePopup: () => void;
  hideSubmitBox?: boolean;
  hubspotFormId: string;
  isOpen: boolean;
  onSubmit?: (email: string) => void;
  onEmailCapture?: (email: string) => void;
  successMessage?: string;
  title: React.ReactElement | string;
}

export const EmailCapture: React.FC<React.PropsWithChildren<EmailCaptureProps>> = ({
  actionText,
  children,
  description,
  handleClosePopup,
  hideSubmitBox,
  hubspotFormId,
  isOpen,
  onEmailCapture,
  onSubmit,
  successMessage,
  title,
}) => {
  const session = React.useContext(SessionContext);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    const initialEmail =
      session.getManager(UserManager).getUser()?.email ||
      localStorage.getItem('mob-email') ||
      localStorage.getItem('email');
    setEmail(initialEmail ?? '');
  }, [session]);

  const handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value);
  const handleOnKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = useCallback(() => {
    if (onSubmit) {
      onSubmit(email);
    }
    setIsLoading(true);
    setError(null);
    if (!emailRegEx.test(email)) {
      setError('Invalid email.');
      setIsLoading(false);
      return;
    }
    fetch(`https://api.hsforms.com/submissions/v3/integration/submit/2786381/${hubspotFormId}`, {
      body: JSON.stringify({
        context: {
          pageName: document.title,
          pageUri: typeof window !== 'undefined' ? window.location.href : undefined,
        },
        fields: [
          {
            name: 'email',
            value: email,
          },
        ],
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    })
      .then(res => res.json())
      .then(res => {
        if (res.status === 'error') {
          if (res.errors[0].errorType === 'REQUIRED_FIELD') {
            setError('This field is required.');
          } else if (res.errors[0].errorType === 'INVALID_EMAIL') {
            setError('Invalid email.');
          }
          setIsLoading(false);
        } else if (res.inlineMessage) {
          onEmailCapture && onEmailCapture(email);
          const event = new CustomEvent('form_submit', { detail: { email, hubspotFormId } });
          document.dispatchEvent(event);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setError('Invalid email');
      });
  }, [email, hubspotFormId, onEmailCapture, onSubmit, setError, setIsLoading]);

  return (
    <Dialog
      closeOnOutsideClick={true}
      customButtons={
        !hideSubmitBox && (
          <div className="flex flex-col items-center w-full">
            <Button className="w-full mt-2" isLoading={isLoading} onClick={handleSubmit} variant="primary">
              <div className="font-semibold text-center w-full">{actionText}</div>
            </Button>
            <button className="mt-0.5 py-0.5 text-center text-sm font-normal w-full" onClick={handleClosePopup}>
              Cancel
            </button>
          </div>
        )
      }
      lockScroll={true}
      onRequestClose={handleClosePopup}
      open={isOpen}
      title={title}
    >
      <EmailCaptureWrapper className="email-capture-wrapper">
        <div className="w-full">
          {description && (
            <p className="leading-5 font-manrope text-left text-sm mb-1 font-medium pb-2">{description}</p>
          )}
          <div>
            {!hideSubmitBox && (
              <>
                <label className="text-sm mb-4">EMAIL*</label>
                <div
                  className={classNames(
                    'bg-gray-300 bg-opacity-10 border box-border box-shadow h-9 inline-flex items-center rounded w-full',
                    { 'border-gray-400': !error, 'border-opacity-70': !error, 'border-red-500': !!error },
                  )}
                >
                  <input
                    className="hubspot-input w-full text-md px-2 font-bold leading-none placeholder-gray-600 bg-transparent border-none ring-0 outline-none"
                    onChange={handleChangeEmail}
                    onKeyUp={handleOnKeyUp}
                    value={email}
                  />
                </div>
              </>
            )}
            {error && (
              <div>
                <span className="text-red-500 error-message">{error}</span>
              </div>
            )}
            {!error && successMessage && (
              <div>
                <span className="success-message">{successMessage}</span>
              </div>
            )}
          </div>
          {children}
        </div>
      </EmailCaptureWrapper>
    </Dialog>
  );
};

export const EmailCaptureWrapper = styled.div`
  &.email-capture-wrapper {
    .success-message {
      color: ${({ theme }) => theme.colorPalette.green400};
    }
    .success-message,
    .error-message {
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
    }
  }
`;
